import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import BLOGS from '../data/blogData';
import '../App.css';

/* ── Content Block Renderers ── */
function ContentBlock({ block }) {
    switch (block.type) {
        case 'heading':
            return (
                <h2 className="text-[clamp(1.25rem,3vw,1.75rem)] font-bold text-on-surface mt-10 mb-4 tracking-tight">
                    {block.text}
                </h2>
            );
        case 'paragraph':
            return (
                <p className="text-[1.0625rem] text-on-surface-variant leading-[1.85] mb-6">
                    {block.text}
                </p>
            );
        case 'code':
            return (
                <div className="my-6 rounded-2xl overflow-hidden bg-[#1e1e2e] shadow-2">
                    {/* Language label */}
                    <div className="flex items-center gap-2 px-5 py-2.5 bg-white/[0.04] border-b border-white/[0.06]">
                        <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                        <span className="ml-auto text-xs font-mono text-white/40 uppercase tracking-wider">
                            {block.language}
                        </span>
                    </div>
                    <pre className="p-5 overflow-x-auto text-[0.875rem] leading-relaxed">
                        <code className="text-[#cdd6f4] font-mono whitespace-pre">{block.text}</code>
                    </pre>
                </div>
            );
        case 'quote':
            return (
                <blockquote className="my-8 pl-6 border-l-4 border-primary/50 bg-primary-container/20 rounded-r-2xl py-5 pr-6">
                    <p className="text-[1.0625rem] text-on-surface italic leading-relaxed">
                        {block.text}
                    </p>
                </blockquote>
            );
        case 'list':
            return (
                <ul className="my-6 space-y-3 pl-1">
                    {block.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-[1.0625rem] text-on-surface-variant leading-relaxed">
                            <span className="mt-2 w-2 h-2 rounded-full bg-primary shrink-0" />
                            <span>{item}</span>
                        </li>
                    ))}
                </ul>
            );
        default:
            return null;
    }
}

/* ════════════════════════════════════
   BLOG POST PAGE
   ════════════════════════════════════ */
export default function BlogPost() {
    const { slug } = useParams();
    const blog = BLOGS.find((b) => b.slug === slug);
    const { dark, toggleTheme } = useTheme();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [slug]);

    /* ── 404 ── */
    if (!blog) {
        return (
            <div className="min-h-screen bg-surface font-sans flex items-center justify-center px-4">
                <div className="text-center">
                    <div className="text-[6rem] font-extrabold gradient-text mb-4">404</div>
                    <h1 className="text-2xl font-bold text-on-surface mb-2">Post Not Found</h1>
                    <p className="text-on-surface-variant mb-8">
                        The blog post you're looking for doesn't exist or has been moved.
                    </p>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 px-7 py-3 bg-primary text-on-primary rounded-full text-[0.9375rem] font-semibold shadow-1 hover:shadow-2 hover:brightness-[1.08] active:scale-[0.97] transition-all duration-200 ease-standard"
                    >
                        <i className="fa-solid fa-arrow-left" /> Back Home
                    </Link>
                </div>
            </div>
        );
    }

    /* ── Blog Post ── */
    return (
        <div className="min-h-screen bg-surface font-sans text-on-surface">
            {/* ── Top Navigation Bar ── */}
            <nav className="fixed top-0 inset-x-0 z-50 flex justify-center p-4 backdrop-blur-[20px] shadow-1" style={{ backgroundColor: 'var(--nav-bg)' }}>
                <div className="max-w-[1200px] w-full flex items-center justify-between">
                    <Link
                        to="/"
                        className="text-xl font-bold text-primary tracking-tight"
                    >
                        R.
                    </Link>
                    <div className="flex items-center gap-1">
                        <button
                            className="w-10 h-10 rounded-full flex items-center justify-center text-lg text-on-surface-variant hover:bg-surface-variant hover:text-on-surface transition-all duration-200 ease-standard"
                            onClick={toggleTheme}
                            aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            <i className={dark ? 'fa-solid fa-sun' : 'fa-solid fa-moon'}></i>
                        </button>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-on-surface-variant hover:bg-surface-variant hover:text-on-surface transition-all duration-200 ease-standard"
                        >
                            <i className="fa-solid fa-arrow-left" /> Back to Home
                        </Link>
                    </div>
                </div>
            </nav>

            {/* ── Hero / Header ── */}
            <header className="pt-28 pb-12 md:pt-36 md:pb-16 px-4 md:px-6 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute inset-0 z-0 overflow-hidden">
                    <div
                        className="absolute w-[600px] h-[600px] rounded-full -top-[200px] -right-[200px] blur-[120px] opacity-40"
                        style={{ background: blog.accentColor }}
                    />
                    <div
                        className="absolute w-[400px] h-[400px] rounded-full -bottom-[100px] -left-[100px] blur-[100px] opacity-20"
                        style={{ background: blog.accentColor }}
                    />
                </div>

                <div className="relative z-10 max-w-[760px] mx-auto">
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-6 animate-fade-up">
                        {blog.tags.map((tag) => (
                            <span
                                key={tag}
                                className="px-3 py-1 bg-primary-container text-on-primary-container rounded-full text-xs font-semibold uppercase tracking-wider"
                            >
                                {tag}
                            </span>
                        ))}
                    </div>

                    {/* Title */}
                    <h1 className="text-[clamp(2rem,5vw,3.25rem)] font-extrabold leading-[1.1] tracking-[-1px] text-on-surface mb-6 animate-fade-up-1">
                        {blog.title}
                    </h1>

                    {/* Meta */}
                    <div className="flex items-center gap-6 text-[0.9375rem] text-on-surface-variant animate-fade-up-2">
                        <span className="flex items-center gap-2">
                            <i className="fa-regular fa-calendar" /> {blog.date}
                        </span>
                        <span className="flex items-center gap-2">
                            <i className="fa-regular fa-clock" /> {blog.readTime}
                        </span>
                    </div>

                    {/* Accent divider */}
                    <div
                        className="w-20 h-1 rounded-full mt-8 animate-fade-up-3"
                        style={{ background: blog.accentColor }}
                    />
                </div>
            </header>

            {/* ── Article Content ── */}
            <article className="px-4 md:px-6 pb-20">
                <div className="max-w-[760px] mx-auto">
                    {blog.content.map((block, i) => (
                        <ContentBlock key={i} block={block} />
                    ))}
                </div>
            </article>

            {/* ── Bottom CTA ── */}
            <div className="border-t border-outline-variant">
                <div className="max-w-[760px] mx-auto px-4 md:px-6 py-12 flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div>
                        <p className="text-on-surface-variant text-sm mb-1">Enjoyed this article?</p>
                        <p className="text-on-surface font-semibold">Check out more posts on the blog.</p>
                    </div>
                    <Link
                        to="/#blog"
                        className="inline-flex items-center gap-2 px-7 py-3 bg-primary text-on-primary rounded-full text-[0.9375rem] font-semibold shadow-1 hover:shadow-2 hover:brightness-[1.08] active:scale-[0.97] transition-all duration-200 ease-standard"
                    >
                        <i className="fa-solid fa-pen-nib" /> All Posts
                    </Link>
                </div>
            </div>

            {/* ── Footer ── */}
            <footer className="bg-inverse-surface p-6 text-center border-t border-white/5">
                <p className="text-inverse-on-surface text-[0.8125rem]">
                    © {new Date().getFullYear()} Ramesh Alagumalai.
                </p>
            </footer>
        </div>
    );
}
